import React, { Component } from 'react';
import { Form, Checkbox } from 'semantic-ui-react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { NotificationCategory } from 'stores/users/users-store';
import { getNotificationCategoriesForRoles } from 'utils/notification-role-filter';

interface NotificationCategoriesSelectorProps extends WithTranslation {
  emailNotifications: boolean;
  selectedNotificationTypes: NotificationCategory[];
  onEmailNotificationsChange: (enabled: boolean) => void;
  onSelectedNotificationTypesChange: (categories: NotificationCategory[]) => void;
  disabled?: boolean;
  userRoles?: string[]; // Array of role names for filtering
  layout?: 'single-column' | 'two-columns'; // Layout control: single column or two columns
  // Global Reporting specific props
  isGlobalReporting?: boolean;
  reportingGlobalNotifications?: boolean;
  onReportingGlobalNotificationsChange?: (enabled: boolean) => void;
  // Power Instructor specific props
  isPowerInstructor?: boolean;
  powerInstructorNotifications?: boolean;
  onPowerInstructorNotificationsChange?: (enabled: boolean) => void;
}

interface NotificationCategoriesSelectorState {
  localEmailNotifications: boolean;
  localSelectedNotificationTypes: NotificationCategory[];
}

class NotificationCategoriesSelector extends Component<NotificationCategoriesSelectorProps, NotificationCategoriesSelectorState> {
  constructor(props: NotificationCategoriesSelectorProps) {
    super(props);

    // Apply synchronization logic and role-based filtering
    const filteredCategories = this.getFilteredCategories(props.selectedNotificationTypes, props.userRoles);
    const syncedNotificationTypes = this.getSyncedNotificationTypes(props.emailNotifications, filteredCategories);

    this.state = {
      localEmailNotifications: props.emailNotifications,
      localSelectedNotificationTypes: syncedNotificationTypes
    };
  }

  componentDidUpdate(prevProps: NotificationCategoriesSelectorProps) {
    if (
      prevProps.emailNotifications !== this.props.emailNotifications ||
      JSON.stringify(prevProps.selectedNotificationTypes) !== JSON.stringify(this.props.selectedNotificationTypes) ||
      JSON.stringify(prevProps.userRoles) !== JSON.stringify(this.props.userRoles)
    ) {
      const filteredCategories = this.getFilteredCategories(this.props.selectedNotificationTypes, this.props.userRoles);
      const syncedNotificationTypes = this.getSyncedNotificationTypes(this.props.emailNotifications, filteredCategories);

      this.setState({
        localEmailNotifications: this.props.emailNotifications,
        localSelectedNotificationTypes: syncedNotificationTypes
      });
    }
  }

  private getFilteredCategories = (categories: NotificationCategory[], userRoles?: string[]): NotificationCategory[] => {
    // If no roles provided, return all categories (backward compatibility)
    if (!userRoles || userRoles.length === 0) {
      return categories;
    }

    // Get categories for the user's roles, maintaining selection state from existing categories
    return getNotificationCategoriesForRoles(userRoles, categories, false);
  };

  private getSyncedNotificationTypes = (
    emailNotifications: boolean,
    selectedNotificationTypes: NotificationCategory[]
  ): NotificationCategory[] => {
    // If emailNotifications is enabled but children are disabled, enable the children
    return emailNotifications && selectedNotificationTypes.length > 0
      ? selectedNotificationTypes.map(category => ({
          ...category,
          isSelected: true
        }))
      : selectedNotificationTypes;
  };

  private handleSelectAllNotificationTypes = (_: any, data: any) => {
    const { checked } = data;
    const updatedCategories = this.state.localSelectedNotificationTypes.map(category => ({
      ...category,
      isSelected: checked
    }));

    this.setState({
      localEmailNotifications: checked,
      localSelectedNotificationTypes: updatedCategories
    });

    // Notify parent components
    this.props.onEmailNotificationsChange(checked);
    this.props.onSelectedNotificationTypesChange(updatedCategories);

    // Handle Reporting Global Notifications dependency
    if (this.props.isGlobalReporting && this.props.onReportingGlobalNotificationsChange) {
      if (!checked) {
        // If turning off all notifications, also turn off Reporting Global Notifications
        this.props.onReportingGlobalNotificationsChange(false);
      } else {
        // If turning on all notifications, also turn on Reporting Global Notifications
        this.props.onReportingGlobalNotificationsChange(true);
      }
    }

    // Handle Power Instructor Notifications dependency
    if (this.props.isPowerInstructor && this.props.onPowerInstructorNotificationsChange) {
      if (!checked) {
        // If turning off all notifications, also turn off Power Instructor Notifications
        this.props.onPowerInstructorNotificationsChange(false);
      } else {
        // If turning on all notifications, also turn on Power Instructor Notifications
        this.props.onPowerInstructorNotificationsChange(true);
      }
    }
  };

  private handleNotificationCategoryChange = (categoryId: number) => (_: any, data: any) => {
    const { checked } = data;
    const updatedCategories = this.state.localSelectedNotificationTypes.map(category =>
      category.id === categoryId ? { ...category, isSelected: checked } : category
    );

    // Update master checkbox: only true if ALL categories are selected
    const allSelected = updatedCategories.every(category => category.isSelected);

    this.setState({
      localSelectedNotificationTypes: updatedCategories,
      localEmailNotifications: allSelected
    });

    // Notify parent components
    this.props.onEmailNotificationsChange(allSelected);
    this.props.onSelectedNotificationTypesChange(updatedCategories);

    // Special handling for Request and Event category
    const changedCategory = this.state.localSelectedNotificationTypes.find(cat => cat.id === categoryId);
    if (
      changedCategory &&
      changedCategory.name === 'Request and Event' &&
      this.props.isGlobalReporting &&
      this.props.onReportingGlobalNotificationsChange
    ) {
      if (!checked) {
        // If Request and Event is being unchecked, also uncheck Reporting Global Notifications
        this.props.onReportingGlobalNotificationsChange(false);
      } else {
        // If Request and Event is being checked, also check Reporting Global Notifications
        this.props.onReportingGlobalNotificationsChange(true);
      }
    }

    // Special handling for Request and Event category - Power Instructor
    if (
      changedCategory &&
      changedCategory.name === 'Request and Event' &&
      this.props.isPowerInstructor &&
      this.props.onPowerInstructorNotificationsChange
    ) {
      if (!checked) {
        // If Request and Event is being unchecked, also uncheck Power Instructor Notifications
        this.props.onPowerInstructorNotificationsChange(false);
      } else {
        // If Request and Event is being checked, also check Power Instructor Notifications
        this.props.onPowerInstructorNotificationsChange(true);
      }
    }
  };

  render() {
    const { t, disabled = false, layout = 'two-columns' } = this.props;
    const { localEmailNotifications, localSelectedNotificationTypes } = this.state;

    // Determine grid layout based on layout prop
    const gridStyle =
      layout === 'single-column'
        ? { marginLeft: '20px', marginTop: '10px', display: 'flex', flexDirection: 'column' as const }
        : { marginLeft: '20px', marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr' };

    return (
      <>
        <div className="user-settings__section-label">{t('Email Notifications')}</div>
        <div className="user-settings__inputs-container">
          <div className="child-checkboxes" style={gridStyle}>
            <Form.Field className="user-settings_checkbox-field" style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                className="user-modal__checkbox-toggle"
                toggle
                checked={localEmailNotifications}
                onChange={this.handleSelectAllNotificationTypes}
                disabled={disabled}
              />
              <label>{t('Active all email Notifications')}</label>
            </Form.Field>
            {layout === 'two-columns' && <div></div>}
            {localSelectedNotificationTypes.map(category => (
              <div key={category.id}>
                <React.Fragment key={category.id}>
                  <Form.Field className="user-settings_checkbox-field child-checkbox" style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      className="user-modal__checkbox-toggle"
                      toggle
                      checked={category.isSelected}
                      onChange={this.handleNotificationCategoryChange(category.id)}
                      disabled={disabled}
                    />
                    <label style={{ margin: 0 }}>{t(category.name)}</label>
                  </Form.Field>
                  {/* Global Reporting sub-checkbox under Request and Event */}
                  {this.props.isGlobalReporting && category.name === 'Request and Event' && (
                    <>
                      {layout === 'two-columns' && <div></div>}
                      <div style={{ marginLeft: '20px' }}>
                        <Form.Field
                          className="user-settings_checkbox-field child-checkbox"
                          style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Checkbox
                            className="user-modal__checkbox-toggle"
                            toggle
                            checked={category.isSelected ? this.props.reportingGlobalNotifications ?? true : false}
                            onChange={(_, { checked }) => {
                              // Only allow changes if parent category is selected
                              if (category.isSelected) {
                                this.props.onReportingGlobalNotificationsChange?.(checked || false);
                              }
                            }}
                            disabled={disabled || !category.isSelected}
                          />
                          <label style={{ margin: 0, opacity: category.isSelected ? 1 : 0.5 }}>{t('Reporting Global Notifications')}</label>
                        </Form.Field>
                      </div>
                      {layout === 'two-columns' && <div></div>}
                    </>
                  )}
                  {/* Power Instructor sub-checkbox under Request and Event */}
                  {this.props.isPowerInstructor && category.name === 'Request and Event' && (
                    <>
                      {layout === 'two-columns' && <div></div>}
                      <div style={{ marginLeft: '20px' }}>
                        <Form.Field
                          className="user-settings_checkbox-field child-checkbox"
                          style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Checkbox
                            className="user-modal__checkbox-toggle"
                            toggle
                            checked={category.isSelected ? this.props.powerInstructorNotifications ?? false : false}
                            onChange={(_, { checked }) => {
                              // Only allow changes if parent category is selected
                              if (category.isSelected) {
                                this.props.onPowerInstructorNotificationsChange?.(checked || false);
                              }
                            }}
                            disabled={disabled || !category.isSelected}
                          />
                          <label style={{ margin: 0, opacity: category.isSelected ? 1 : 0.5 }}>
                            {t("Team's Notifications (Power Instructor)")}
                          </label>
                        </Form.Field>
                      </div>
                      {layout === 'two-columns' && <div></div>}
                    </>
                  )}
                </React.Fragment>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }
}

export default withTranslation()(NotificationCategoriesSelector);
